import { useEffect, useState } from 'react';
import { useCalibrationState, SENSOR_UNITS } from '../../context/CalibrationPageContext';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';


function NewSensorDetails() {
  const {
    selectedSensorType,
    serialNumber,
    calFactor,
    calFactorUnit,
    testDate,
    setSelectedSensorType,
    setSerialNumber,
    setCalFactor,
    setCalFactorUnit,
    setTestDate,
    handleSensorChange,
    resetNewSensorDetails,
    addSensor,
  } = useCalibrationState();

  const unitOptions = SENSOR_UNITS[selectedSensorType] || [''];

  const [serialNumberError, setSerialNumberError] = useState(false);
  const [calFactorError, setCalFactorError] = useState(false);
  const [testDateError, setTestDateError] = useState(false);
  const [trySubmit, setTrySubmit] = useState(false);

  useEffect(() => {
    if (trySubmit) {
      setSerialNumberError(serialNumber.trim() === '');
      setCalFactorError(calFactor.trim() === '');
      setTestDateError(testDate.trim() === '');
    }
  }, [trySubmit, serialNumber, calFactor, testDate]);

  const handleAddSensor = () => {
    setTrySubmit(true);
    if (serialNumber.trim() !== '' && calFactor.trim() !== '' && testDate.trim() !== '') {
      addSensor();
      setTrySubmit(false);
    }
  }

  return (
    <Box
      sx={{
        p: 1,
        width: { xs: '100%', md: '30%' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Box
        sx={{
          height: { xs: 'auto', md: 'calc(100% - 36px)' },
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pt: 1,
        }}
      >
        <Stack
          direction='column'
          spacing={1.5}
        >
          <FormControl
            size='small'
            display='flex'
            fullWidth
            sx={{
              '& .MuiSelect-select': {
                fontSize: '14px',
                py: 1
              }
            }}
          >
            <InputLabel>Select Sensor Type</InputLabel>
            <Select
              value={selectedSensorType}
              label='Select Sensor Type'
              MenuProps={{ disableScrollLock: true }}
              onChange={handleSensorChange}
            >
              <MenuItem value='Loadcells'>Load Cell</MenuItem>
              <MenuItem value='PressureGauges'>Pressure Gauge</MenuItem>
              <MenuItem value='LVDTSensors'>LVDT Sensor</MenuItem>
              <MenuItem value='StrainGauges'>Strain Gauge</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size='small'
            label='Serial Number'
            value={serialNumber}
            error={serialNumberError}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setSerialNumber(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '14px',
                py: 1
              }
            }}
          />

          <TextField
            size='small'
            label='Calibration Factor'
            value={calFactor}
            error={calFactorError}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setCalFactor(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {unitOptions.length > 1 ? (
                    <ToggleButtonGroup
                      size='small'
                      exclusive
                      value={calFactorUnit}
                      // Ignore null: clicking the active button would otherwise
                      // deselect it and leave the reading with no unit at all.
                      onChange={(_, unit) => { if (unit !== null) setCalFactorUnit(unit); }}
                      sx={{
                        '& .MuiToggleButton-root': {
                          fontSize: '10px',
                          lineHeight: 1.1,
                          py: 0.3,
                          px: 0.7,
                          textTransform: 'none',
                          whiteSpace: 'nowrap',
                        },
                        // Selected unit uses the site accent blue (primary.main,
                        // the same one on the active tab and the add button).
                        '& .MuiToggleButton-root.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: '#ffffff',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      }}
                    >
                      {unitOptions.map((unit) => (
                        <ToggleButton key={unit} value={unit}>
                          {unit}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  ) : (
                    <Typography
                      sx={{ fontSize: '12px', color: 'text.secondary', whiteSpace: 'nowrap' }}
                    >
                      {unitOptions[0]}
                    </Typography>
                  )}
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '14px',
                py: 1
              }
            }}
          />

          <TextField
            size='small'
            type='date'
            label='Test Date'
            value={testDate}
            error={testDateError}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setTestDate(e.target.value)}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '14px',
                py: 1
              }
            }}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          height: '28px',
          mt: 1
        }}
      >
        <Stack
          direction='row'
          display='flex'
          justifyContent='space-between'
        >
          <Button
            size='small'
            variant='outlined'
            onClick={resetNewSensorDetails}
            sx={{
              height: '28px',
              fontSize: '12px',
              py: 0.5,
              px: { md: 1, sm: 0.5 }
            }}
          >
            Cancel
          </Button>

          <Button
            size='small'
            variant='contained'
            onClick={handleAddSensor}
            sx={{
              height: '28px',
              fontSize: '12px',
              py: 0.5,
              px: 1
            }}
          >
            <AddIcon sx={{ fontSize: { md: 24, sm: 20 } }} />
          </Button>
        </Stack>
      </Box>

    </Box>

  );
}

export default NewSensorDetails;